import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub, hubBaseUrl } from '@/lib/hub-client';
import { IntegrationsView, type CallbackBanner } from './integrations-view';
import { McpConnectionsSection, MCP_CALLBACK_ERROR_COPY } from './mcp-connections';

// M16 task-08 — Settings → Integrations container (CONN-08; HUB-075 §4).
// A PERSONAL credential surface: lives in the auth-gated hub-root subtree,
// explicitly NOT in the admin dashboard. Data flows through the typed `hub`
// client; the pure rendering lives in ./integrations-view.tsx.

export const CONNECTORS_QUERY_KEY = ['connectors'] as const;

/** The Drive consent flow entry — a browser redirect chain, NOT an API call. */
export function driveStartUrl(): string {
  return `${hubBaseUrl}/connectors/google-drive/start`;
}

/** Navigation seam for Connect/Reconnect: a FULL-PAGE navigation to the hub. */
export function goToDriveStart(): void {
  window.location.href = driveStartUrl();
}

// --- callback landing (?connected= / ?error= set by the hub's /callback) ---

export const CALLBACK_SUCCESS_COPY =
  'Google Drive connected. Designate a folder below to start syncing.';

export const CALLBACK_ERROR_COPY: Record<string, string> = {
  drive_denied: 'Google Drive access was denied, so nothing was connected. Try again and allow access.',
  no_refresh_token: 'Google did not return a usable credential. Try connecting again.',
};

export function bannerFromParams(params: URLSearchParams): CallbackBanner | null {
  // HUB-107 — an MCP callback names its server (`connected=<slug>` or
  // `error=…&server=<slug>`); the Drive copy below stays Drive-only.
  const connected = params.get('connected');
  if (connected && connected !== 'google-drive') {
    return { kind: 'success', text: `${connected} connected. The copilot can now use it for you.` };
  }
  const mcpError = params.get('error');
  if (mcpError && MCP_CALLBACK_ERROR_COPY[mcpError]) {
    return { kind: 'error', text: MCP_CALLBACK_ERROR_COPY[mcpError] };
  }
  if (connected === 'google-drive') {
    return { kind: 'success', text: CALLBACK_SUCCESS_COPY };
  }
  const error = params.get('error');
  if (error) {
    return {
      kind: 'error',
      text: CALLBACK_ERROR_COPY[error] ?? 'Connecting Google Drive failed. Try again.',
    };
  }
  return null;
}

// --- API error → inline copy (codes from POST /connectors/:id/folders) ---

export const ADD_FOLDER_ERROR_COPY: Record<string, string> = {
  invalid_folder_link: 'That does not look like a Drive folder link or ID.',
  folder_not_found: 'Drive does not know this folder, or this account cannot see it.',
  not_a_folder: 'That Drive item is not a folder.',
  already_designated: 'That folder is already designated.',
  credential_invalid: 'The Drive credential is invalid — reconnect to resume syncing.',
};

export function connectorErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof HubClientError && err.code && ADD_FOLDER_ERROR_COPY[err.code]) {
    return ADD_FOLDER_ERROR_COPY[err.code];
  }
  return err instanceof Error ? err.message : fallback;
}

// --- actions (exported seam so the no-jsdom test harness can drive them) ---

type ConnectorClient = Pick<
  typeof hub,
  'connectorsAddFolder' | 'connectorsRemoveFolder' | 'connectorsSyncNow' | 'connectorsDelete'
>;

export interface ConnectorActionsDeps {
  connectionId: string;
  /** Invalidates the connectors query after a successful mutation. */
  invalidate: () => unknown;
  client?: ConnectorClient;
}

/**
 * The mutation handlers behind the page's buttons. Each returns `null` on
 * success or an inline error message. `connectorsDelete` is reachable ONLY
 * through `disconnect()`, which the container wires to the ConfirmDialog's
 * onConfirm — request/cancel never touch the client.
 */
export function createConnectorActions({
  connectionId,
  invalidate,
  client = hub,
}: ConnectorActionsDeps) {
  return {
    async addFolder(folder: string): Promise<string | null> {
      try {
        await client.connectorsAddFolder({ params: { id: connectionId }, body: { folder } });
        await invalidate();
        return null;
      } catch (err) {
        return connectorErrorMessage(err, 'Could not add the folder.');
      }
    },
    async removeFolder(folderId: string): Promise<string | null> {
      try {
        await client.connectorsRemoveFolder({ params: { id: connectionId, folderId } });
        await invalidate();
        return null;
      } catch (err) {
        return connectorErrorMessage(err, 'Could not remove the folder.');
      }
    },
    async syncNow(): Promise<string | null> {
      try {
        await client.connectorsSyncNow({ params: { id: connectionId } });
        await invalidate();
        return null;
      } catch (err) {
        return connectorErrorMessage(err, 'Could not queue the sync.');
      }
    },
    async disconnect(): Promise<string | null> {
      try {
        await client.connectorsDelete({ params: { id: connectionId } });
        await invalidate();
        return null;
      } catch (err) {
        return connectorErrorMessage(err, 'Could not disconnect.');
      }
    },
  };
}

// --- page ---

export function Integrations() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read the callback-landing params during the FIRST render (not an effect)
  // so the banner is part of the initial markup; the mount effect below only
  // strips the params from the URL.
  const [banner] = useState<CallbackBanner | null>(() => bannerFromParams(searchParams));
  useEffect(() => {
    if (!searchParams.has('connected') && !searchParams.has('error')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('error');
    next.delete('server');
    setSearchParams(next, { replace: true });
    // Run once on mount — the params are a one-shot landing signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: CONNECTORS_QUERY_KEY,
    queryFn: () => hub.connectorsList(),
    // No push channel for sync progress — poll while the page is mounted.
    refetchInterval: 10_000,
  });
  // V1 is Drive-only, so the list holds at most one connection.
  const connection = query.data?.connections[0] ?? null;
  const connectionId = connection?.id ?? null;

  const actions = useMemo(
    () =>
      connectionId
        ? createConnectorActions({
            connectionId,
            invalidate: () => queryClient.invalidateQueries({ queryKey: CONNECTORS_QUERY_KEY }),
          })
        : null,
    [connectionId, queryClient],
  );

  const [folderInput, setFolderInput] = useState('');
  const [addFolderBusy, setAddFolderBusy] = useState(false);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [removingFolderId, setRemovingFolderId] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);

  const handleAddFolder = async () => {
    const folder = folderInput.trim();
    if (!actions || !folder || addFolderBusy) return;
    setAddFolderBusy(true);
    setFolderError(null);
    const error = await actions.addFolder(folder);
    setAddFolderBusy(false);
    if (error) setFolderError(error);
    else setFolderInput('');
  };

  const handleRemoveFolder = async (folderId: string) => {
    if (!actions || removingFolderId) return;
    setRemovingFolderId(folderId);
    setFolderError(null);
    const error = await actions.removeFolder(folderId);
    setRemovingFolderId(null);
    if (error) setFolderError(error);
  };

  const handleSyncNow = async () => {
    if (!actions || syncBusy) return;
    setSyncBusy(true);
    setActionError(null);
    const error = await actions.syncNow();
    setSyncBusy(false);
    if (error) setActionError(error);
  };

  const handleConfirmDisconnect = async () => {
    if (!actions || disconnectBusy) return;
    setDisconnectBusy(true);
    setActionError(null);
    const error = await actions.disconnect();
    setDisconnectBusy(false);
    setDisconnectOpen(false);
    if (error) setActionError(error);
  };

  return (
    <>
      <IntegrationsView
        loading={query.isPending}
        loadError={query.error ? query.error.message : null}
        connection={connection}
        banner={banner}
        onConnect={goToDriveStart}
        folderInput={folderInput}
        onFolderInputChange={setFolderInput}
        onAddFolder={() => void handleAddFolder()}
        addFolderBusy={addFolderBusy}
        folderError={folderError}
        onRemoveFolder={(folderId) => void handleRemoveFolder(folderId)}
        removingFolderId={removingFolderId}
        onSyncNow={() => void handleSyncNow()}
        syncBusy={syncBusy}
        actionError={actionError}
        disconnectOpen={disconnectOpen}
        disconnectBusy={disconnectBusy}
        onRequestDisconnect={() => setDisconnectOpen(true)}
        onConfirmDisconnect={() => void handleConfirmDisconnect()}
        onCancelDisconnect={() => setDisconnectOpen(false)}
      />
      <div className="mt-8">
        <McpConnectionsSection />
      </div>
    </>
  );
}
